import { DealPipelineInvestmentsQuery, DealPipelineInvestmentWorkflowStateFiltersQuery } from '@/__generated__/graphql';
import { IItems, TableColumns } from '@/components/table/types';
import { useResponsive } from '@/hooks/use-responsive';
import { ApolloError, useMutation } from '@apollo/client';
import { useState } from 'react';
import { NavigateOptions } from 'react-router-dom';
import { Loader } from '../../loaders/loader';
import ChangeAssignee from '../modals/changeAssignee';
import DeleteInvestment from '../modals/deleteInvestment';
import Terminate from '../modals/terminate';
import UpdateSecurityStatus from '../modals/updateSecurityStatus';
import { DEAL_PIPELINE_INVESTMENTS_QUERY, DEAL_PIPELINE_PAGE_EXECUTE_WORKFLOW_TRANSITION_MUTATION } from '../queries';
import DealPipelineMobileTable from './mobileTable';
import { DealPipelineTable } from './tableBody';

interface TableProps {
  columnsList: TableColumns[];
  tableRows: DealPipelineInvestmentsQuery['Investments']['data'];
  tableLoading: boolean;
  workflowStates: DealPipelineInvestmentWorkflowStateFiltersQuery['InvestmentWorkflowStateFilters'];
  forceRecalculateTableFiltersHeight: any;
  openPage: (path: string, options?: NavigateOptions) => void;
  openMobileModal: (data: DealPipelineInvestmentsQuery['Investments']['data'][number], modalType: string) => void;
}

const Table = ({
  columnsList,
  tableRows,
  tableLoading,
  workflowStates,
  forceRecalculateTableFiltersHeight,
  openPage,
  openMobileModal
}: TableProps) => {
  const { isMobile } = useResponsive();

  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [currentRow, setCurrentRow] = useState<DealPipelineInvestmentsQuery['Investments']['data'][number] | null>(null);
  const [animatingRowId, setAnimatingRowId] = useState<string | null>(null);

  const [executeWorkflowTransition] = useMutation(DEAL_PIPELINE_PAGE_EXECUTE_WORKFLOW_TRANSITION_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [DEAL_PIPELINE_INVESTMENTS_QUERY]
  });

  const handleFlashRowAnimation = ({ rowId }: { rowId: string | null }) => {
    setAnimatingRowId(rowId);
  };

  const handleWorkflowTransition = async (row: DealPipelineInvestmentsQuery['Investments']['data'][number], index: number) => {
    try {
      if (row.availableWorkflowTransitions[index].name === 'deleteInvestment') {
        openModalWindow('delete-investment', row);
        return;
      }

      if (row.availableWorkflowTransitions[index].name === 'terminate') {
        openModalWindow('terminate', row);
        return;
      }

      handleFlashRowAnimation({ rowId: row.id });

      await executeWorkflowTransition({
        variables: {
          data: {
            instanceId: row.workflowInstance.id,
            transitionId: row.availableWorkflowTransitions[index].id
          }
        }
      });

      handleFlashRowAnimation({ rowId: null });
    } catch (err) {
      const error = err as ApolloError;
      handleFlashRowAnimation({ rowId: null });

      if (error.message.includes('required field errors')) {
        openPage(`/dealPipeline/modify?backToTitle=Manager Pipeline&investment=${encodeURIComponent(row.id)}&modalPageType=basicInfo`, {
          state: {
            instanceId: row.workflowInstance.id,
            transitionId: row.availableWorkflowTransitions[index].id,
            status: error.message.split(' ')[0]
          }
        });
      }
    }
  };

  const actionsByStatus = (row: DealPipelineInvestmentsQuery['Investments']['data'][number]): IItems[] => {
    const availableWorkflowTransitions =
      row?.availableWorkflowTransitions?.map((transition, index) => ({
        name: transition.label,
        onClick: () => handleWorkflowTransition(row, index)
      })) || [];

    return [
      ...(row?.workflowInstance?.currentState?.name !== 'terminated'
        ? [
            {
              name: 'Edit',
              onClick: () => openPage(`/dealPipeline/modify?backToTitle=Manager Pipeline&investment=${encodeURIComponent(row.id)}`)
            }
          ]
        : []),
      ...(availableWorkflowTransitions?.length ? availableWorkflowTransitions : []),
      {
        name: 'Update Status',
        onClick: () => openModalWindow('update-security-status', row)
      },
      {
        name: 'View Investor Interest',
        onClick: () => openPage(`/dealPipeline/${encodeURIComponent(row.id)}/${encodeURIComponent(row.name)}?backToTitle=Manager Pipeline`)
      },
      ...(row?.workflowInstance?.currentState?.name !== 'terminated'
        ? [
            {
              name: 'Change Assignee',
              onClick: () => openModalWindow('change-assignee', row)
            }
          ]
        : [])
    ];
  };

  const openModalWindow = (type: string, row: DealPipelineInvestmentsQuery['Investments']['data'][number]) => {
    setModalWindow({ type, isOpen: true });
    setCurrentRow(row);
  };

  const closeModalWindow = () => {
    setModalWindow({ ...modalWindow, isOpen: false });
    setCurrentRow(null);
  };

  const renderDialog = (type: string) => {
    switch (type) {
      case 'terminate':
        return <Terminate isOpen={modalWindow.isOpen} onClose={closeModalWindow} currentRow={currentRow} />;
      case 'change-assignee':
        return <ChangeAssignee isOpen={modalWindow.isOpen} onClose={closeModalWindow} currentRow={currentRow} />;
      case 'delete-investment':
        return <DeleteInvestment isOpen={modalWindow.isOpen} onClose={closeModalWindow} currentRow={currentRow} />;
      case 'update-security-status':
        return (
          <UpdateSecurityStatus
            isOpen={modalWindow.isOpen}
            onClose={closeModalWindow}
            investmentId={currentRow?.id ?? ''}
            workflowStates={workflowStates}
            source="dealPipeline"
            openPage={openPage}
          />
        );
      default:
        return <></>;
    }
  };

  if (isMobile) {
    return (
      <div>
        {renderDialog(modalWindow.type)}
        {tableLoading ? (
          <Loader />
        ) : (
          <DealPipelineMobileTable
            columnsList={columnsList}
            tableRows={tableRows}
            openPage={openPage}
            actionsByStatus={actionsByStatus}
            openMobileModal={openMobileModal}
          />
        )}
      </div>
    );
  }

  return (
    <>
      {renderDialog(modalWindow.type)}
      <DealPipelineTable
        columnsList={columnsList}
        tableRows={tableRows}
        loading={tableLoading}
        animatingRowId={animatingRowId}
        forceRecalculateTableFiltersHeight={forceRecalculateTableFiltersHeight}
        openPage={openPage}
        actionsByStatus={actionsByStatus}
      />
    </>
  );
};

export default Table;
